import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users } from "lucide-react";
import { XIcon } from "@/components/ui/SocialIcons";

export function AboutTeamSection() {
  const team = [
    {
      name: "Amit Verma",
      role: "Operations & Tech",
      twitter: "whocares0412",
      bio: "Building Metefy's digital identity platform to connect items with their owners.",
      color: "#00AEEF",
      image: "https://api.dicebear.com/9.x/adventurer/svg?seed=Jocelyn",
      ariaLabel: "Amit Verma - Operations & Tech"
    },
    {
      name: "Reema",
      role: "Brand Management & Social Media",
      twitter: "_reema_art_10",
      bio: "Sharing stories of how Metefy's digital identity helps return lost items.",
      color: "#FFB3C1",
      image: "https://api.dicebear.com/9.x/adventurer/svg?seed=Oliver",
      ariaLabel: "Reema - Brand Management & Social Media"
    }
  ];

  // Generate Team Schema for SEO
  const teamSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Metefy",
    "description": "Digital identity solution for lost item recovery",
    "member": team.map(member => ({
      "@type": "Person",
      "name": member.name,
      "jobTitle": member.role,
      "image": member.image,
      "sameAs": member.twitter ? [`https://twitter.com/${member.twitter}`] : []
    }))
  };

  return (
    <section id="team" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <Users className="w-4 h-4 text-foreground" />
            <span className="text-foreground">Our Team</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Meet the <span className="text-primary">Founders</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            Passionate about creating technology that keeps your belongings safe
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {team.map((member, index) => (
            <Card key={index} className="bg-card border border-border hover:border-primary hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-muted">
                  <AvatarImage src={member.image} alt={member.name} />
                  <AvatarFallback 
                    className="text-xl text-white" 
                    style={{ backgroundColor: member.color }}
                  >
                    {member.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-bold text-foreground mb-2">{member.name}</h3>
                <div className="text-primary font-semibold mb-3">{member.role}</div>
                <p className="text-foreground/80 text-sm mb-4">{member.bio}</p>
                {member.twitter && (
                  <div className="flex justify-center">
                    <a 
                      href={`https://twitter.com/${member.twitter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:opacity-80 transition-opacity flex items-center gap-2"
                      aria-label={`Follow ${member.name} on X (Twitter)`}
                    >
                      <XIcon className="h-4 w-4 text-foreground" />
                      @{member.twitter}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Inject Team Schema for SEO */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(teamSchema) }}
        aria-hidden="true"
      />
    </section>
  );
} 